import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { IContainer } from '../../model';
import { deleteContainer } from '../services';

export interface FormConfirmDeleteProps {
  container?: IContainer;
  onConfirm: () => void;
  onCancel: () => void;
}

const FormConfirmDelete: React.FunctionComponent<FormConfirmDeleteProps> = (props) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!props.container) return;

    setIsDeleting(true);
    setError(null);

    try {
      const success = await deleteContainer(props.container.tank_config_id);
      if (success) {
        props.onConfirm();
      } else {
        setError('Unable to delete container. It may be in use or a system default.');
      }
    } catch (error) {
      console.error('Error deleting container:', error);
      setError('An error occurred while deleting the container.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={props.container !== undefined} onClose={props.onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          Are you sure you want to delete the container{' '}
          <Typography component="span" fontWeight="bold">
            {props.container?.tank_name}
          </Typography>
          ?
        </DialogContentText>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This action cannot be undone. System default containers cannot be deleted.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={isDeleting || props.container?.is_system_default}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormConfirmDelete;
